/**
 * Tagesverlauf Types
 * 
 * Shared types for Tagesverlauf component and hook.
 */

export interface TimelineLesson {
    lesson_id: string | null;
    start: string;
    end: string;
    title: string;
    status: string | null;
    notes: string | null;
    diaryEntryText?: string | null;
    derivedCancelled?: boolean;
    cancelReason?: string | null;
    attendanceRecordedByName?: string | null;
    attendanceRecordedAt?: string | null;
    classAbsenceId?: string | null;
    classAbsenceType?: string | null;
    classAbsenceDestination?: string | null;
    classAbsenceStartTime?: string | null;
    classAbsenceEndTime?: string | null;
    classAbsenceAttendanceRequired?: boolean;
    dailyPresenceStatus?: string | null;
    dailyIsTripAttendance?: boolean;
}

export interface LatenessInfo {
    minutes: number;
    excused: boolean;
}

export interface TagesverlaufProps {
    lessons: TimelineLesson[];
    studentId: string;
    onUpdate: (lessons: TimelineLesson[]) => void;
    dateIso?: string;
    defaultOpen?: boolean;
    onOpenDiary?: (lessonId: string, isComplete: boolean) => void;
    showDeleteButton?: boolean;
    onDeleteNote?: () => void;
    /** Pre-fill empty past lessons with this status when the timeline
     *  first loads. Used by the klärung popup to show the intent of
     *  the picked status (e.g. Informiert → absent_excused on every
     *  unstamped past lesson) so the teacher can review + save in one
     *  step instead of clicking "Alle informiert" themselves. */
    autoBulkStatus?: string;
    /** Optional per-status label/colour overrides for the pill that
     *  renders on each lesson row. Callers use this to re-skin the
     *  default STATUS_CONFIG entries in a specific context — e.g.
     *  the Prüfung dialog renders absent_excused as
     *  "Informiert (Krankgemeldet)" / "Informiert (Beurlaubt)" with
     *  an amber pill when the admin has picked one of those statuses,
     *  instead of the default blue "Informiert". The underlying status
     *  value is unchanged — this is rendering only. */
    statusLabelOverrides?: Record<string, { label: string; color: string }>;
    /** Hide the timeline's own save controls (BulkActionBar's top
     *  Speichern + the bottom "Änderungen speichern" button). Used
     *  by parent dialogs that own a single commit gate — the parent
     *  is responsible for persisting the pending lesson changes via
     *  `onPendingChangesChange` + its own save flow. */
    hideSaveControls?: boolean;
    /** Bubble the timeline's pending per-lesson changes (lessonId →
     *  next status) to the parent every time they change. Lets a
     *  parent dialog roll its own unified save that includes both
     *  lesson edits and metadata (SAN, Prüfung, etc.) in one click. */
    onPendingChangesChange?: (changes: Record<string, string>) => void;
    /** Optional lesson_id → resolution-outcome label. When a lesson has
     *  an entry, the row renders the label inline (e.g. under a
     *  cancelled "Entfällt" lesson: "Ausnahmsweise als Gast in „X"").
     *  Lets a teacher see how a Klärung was already resolved without
     *  leaving the Tagesverlauf. Pure display — no behaviour change. */
    resolutionLabelByLessonId?: Record<string, string>;
}

export const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
    present: { label: 'Anwesend', color: 'text-green-700 bg-green-50' },
    late: { label: 'Verspätet', color: 'text-yellow-700 bg-yellow-50' },
    late_excused: { label: 'Verspätet (E)', color: 'text-yellow-700 bg-yellow-50' },
    late_unexcused: { label: 'Verspätet (U)', color: 'text-yellow-700 bg-yellow-50' },
    absent_excused: { label: 'Informiert', color: 'text-blue-700 bg-blue-50' },
    absent_unexcused: { label: 'Abwesend (U)', color: 'text-red-700 bg-red-50' },
    // absent_unclear epic: pre-klärung absences render as orange "Ungeklärt"
    // so the Tagesverlauf doesn't fall through to "noch nicht eingetragen".
    absent_unclear: { label: 'Ungeklärt', color: 'text-orange-800 bg-orange-100' },
};

export const getStatusInfo = (status: string | null, latenessInfo?: LatenessInfo) => {
    const st = String(status || '').toLowerCase();
    if (st === 'late' && latenessInfo) {
        const excLabel = latenessInfo.excused ? '(E)' : '(U)';
        return {
            label: `${latenessInfo.minutes} Min. ${excLabel}`,
            color: latenessInfo.excused ? 'text-yellow-700 bg-yellow-50' : 'text-amber-700 bg-amber-50'
        };
    }
    return STATUS_CONFIG[st] || { label: 'noch nicht eingetragen', color: 'text-gray-500 bg-gray-50' };
};
